// Tiny DOM helpers so the UI reads like markup without a framework.

type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number | boolean | ((e: Event) => void)> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (typeof value === "function") {
      node.addEventListener(key.replace(/^on/, "").toLowerCase(), value as EventListener);
    } else if (key === "class") {
      node.className = String(value);
    } else if (typeof value === "boolean") {
      if (value) node.setAttribute(key, "");
    } else {
      node.setAttribute(key, String(value));
    }
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(child));
  }
  return node;
}

export function clear(node: HTMLElement): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function svgIcon(path: string): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
  p.setAttribute("d", path);
  svg.append(p);
  return svg;
}

/**
 * Whether a URL is one worth following out of the world.
 *
 * Only what a browser would fetch over the network. A `javascript:` URL
 * runs rather than loads, a `file:` one reaches the machine the viewer is
 * on, and a `data:` one can be anything at all — none of them are pages,
 * and all of them arrive from somewhere else.
 */
export function isSafeUrl(url: string): boolean {
  if (!url) return false;
  try {
    const protocol = new URL(url).protocol;
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Follow a link out of the world, if it is one.
 *
 * Every URL here came from somewhere else — a prim somebody built, a
 * parcel somebody owns, a region the viewer happens to be connected to —
 * and none of them are trusted. The new page is cut off from this one.
 */
export function openExternal(url: string): boolean {
  if (!isSafeUrl(url)) return false;
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
}

export function formatTime(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * A moment as a date and a time of day, as About Land gives when land was
 * claimed: "Wed, 30 Jun 2004, 12:57:52", in the reader's own order.
 */
export function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatMoney(symbol: string, amount: number | bigint): string {
  return `${symbol}${Number(amount).toLocaleString()}`;
}

export function loadSetting<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`starfall.${key}`);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function saveSetting(key: string, value: unknown): void {
  try {
    localStorage.setItem(`starfall.${key}`, JSON.stringify(value));
  } catch {
    // storage unavailable
  }
}
